"use client";

import { HIDDEN_CLOSE_AFTER_MS } from "@/config/timing";

export interface StreamEvent {
  type: string;
  slide_id?: string;
  handyout_id?: string;
  presentation_id?: string;
  processing_status?: unknown;
}

export interface StreamSubscriber<E extends StreamEvent = StreamEvent> {
  onEvent: (event: E) => void;
  onResync?: () => void;
}

interface Connection {
  source: EventSource | null;
  subscribers: Set<StreamSubscriber>;
}

const connections = new Map<string, Connection>();

let suspended = false;
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityBound = false;

function open(url: string, connection: Connection) {
  if (connection.source) return;
  const source = new EventSource(url);

  source.onmessage = (message) => {
    let event: StreamEvent;
    try {
      event = JSON.parse(message.data) as StreamEvent;
    } catch {
      return;
    }
    if (!event?.type) return;
    for (const subscriber of [...connection.subscribers]) {
      subscriber.onEvent(event);
    }
  };

  source.onerror = () => {
    console.error(
      `[event-stream] ${url} failed (readyState ${source.readyState})`,
    );
  };

  connection.source = source;
}

function close(connection: Connection) {
  connection.source?.close();
  connection.source = null;
}

function suspendAll() {
  hiddenTimer = null;
  suspended = true;
  for (const connection of connections.values()) close(connection);
}

function resumeAll() {
  if (!suspended) return;
  suspended = false;
  for (const [url, connection] of connections) {
    open(url, connection);
    // Whatever was published while the connection was closed is lost for this
    // client, so the reopen alone would leave a hole: the refetch closes it.
    for (const subscriber of [...connection.subscribers]) {
      subscriber.onResync?.();
    }
  }
}

function handleVisibilityChange() {
  if (document.visibilityState === "hidden") {
    if (hiddenTimer) return;
    hiddenTimer = setTimeout(suspendAll, HIDDEN_CLOSE_AFTER_MS);
    return;
  }
  if (hiddenTimer) {
    clearTimeout(hiddenTimer);
    hiddenTimer = null;
  }
  resumeAll();
}

/** One EventSource per URL, shared by every subscriber in the tab. */
export function subscribeToEventStream(
  url: string,
  subscriber: StreamSubscriber,
): () => void {
  if (!visibilityBound && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    visibilityBound = true;
  }

  let connection = connections.get(url);
  if (!connection) {
    connection = { source: null, subscribers: new Set() };
    connections.set(url, connection);
  }
  connection.subscribers.add(subscriber);
  if (!suspended) open(url, connection);

  return () => {
    const active = connections.get(url);
    if (!active) return;
    active.subscribers.delete(subscriber);
    if (active.subscribers.size > 0) return;
    close(active);
    connections.delete(url);
  };
}
